import { useState } from 'react'
import { Sheet } from '../../ui/parts'
import { formatKcal, caloriesOf, parseGrams } from '../nutrition'
import type { FoodEntry } from '../types'

export interface FoodDraft {
  label: string
  proteinG: number
  carbsG: number
  fatG: number
  alsoSave: boolean
}

/**
 * Calories are never entered — they are derived live from the macros as you type, so the
 * number on screen cannot disagree with what gets saved.
 */
export function AddFoodSheet({
  editing,
  onSave,
  onDelete,
  onClose,
}: {
  editing: FoodEntry | null
  onSave: (draft: FoodDraft) => void
  onDelete?: () => void
  onClose: () => void
}) {
  const [label, setLabel] = useState(editing?.label ?? '')
  const [protein, setProtein] = useState(editing ? String(editing.proteinG) : '')
  const [carbs, setCarbs] = useState(editing ? String(editing.carbsG) : '')
  const [fat, setFat] = useState(editing ? String(editing.fatG) : '')
  const [alsoSave, setAlsoSave] = useState(false)

  const p = parseGrams(protein)
  const c = parseGrams(carbs)
  const f = parseGrams(fat)
  const valid = label.trim() !== '' && p !== null && c !== null && f !== null
  const kcal = valid ? caloriesOf({ proteinG: p!, carbsG: c!, fatG: f! }) : 0

  return (
    <Sheet onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 20 }}>
        {editing ? 'Edit food' : 'Add food'}
      </h2>

      <label className="field">
        <span>Name</span>
        <input
          type="text"
          value={label}
          autoFocus={!editing}
          placeholder="Chicken and rice"
          onChange={(e) => setLabel(e.target.value)}
        />
      </label>

      <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
        <GramField label="Protein" value={protein} onChange={setProtein} parsed={p} />
        <GramField label="Carbs" value={carbs} onChange={setCarbs} parsed={c} />
        <GramField label="Fat" value={fat} onChange={setFat} parsed={f} />
      </div>

      <div className="card" style={{ marginTop: 6, textAlign: 'center' }}>
        <div className="lbl">Calories</div>
        <div className="big">{formatKcal(kcal)}</div>
        <div className="muted" style={{ fontSize: 12 }}>
          Derived from the macros — 4 kcal per gram of protein and carbs, 9 for fat
        </div>
      </div>

      {!editing ? (
        <button
          type="button"
          className="row"
          style={{
            width: '100%',
            marginTop: 12,
            background: 'none',
            border: 0,
            padding: 0,
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
          }}
          onClick={() => setAlsoSave(!alsoSave)}
        >
          <span className={`switch ${alsoSave ? 'on' : ''}`} aria-hidden />
          <span className="grow" style={{ marginLeft: 10 }}>
            <span style={{ fontSize: 14 }}>Save for quick adding</span>
            <span className="muted" style={{ display: 'block', fontSize: 12 }}>
              Keeps the name and macros so you can log it again in one tap
            </span>
          </span>
        </button>
      ) : null}

      <div className="row" style={{ marginTop: 18 }}>
        <button
          type="button"
          className="btn grow"
          disabled={!valid}
          onClick={() =>
            valid && onSave({ label, proteinG: p!, carbsG: c!, fatG: f!, alsoSave })
          }
        >
          {editing ? 'Save changes' : 'Add'}
        </button>
        {editing && onDelete ? (
          <button type="button" className="btn danger" onClick={onDelete}>
            Delete
          </button>
        ) : null}
        <button type="button" className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  )
}

function GramField({
  label,
  value,
  onChange,
  parsed,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  parsed: number | null
}) {
  return (
    <label className="field grow" style={{ marginBottom: 0 }}>
      <span>{label} (g)</span>
      <input
        type="text"
        inputMode="decimal"
        placeholder="0"
        value={value}
        className={value !== '' && parsed === null ? 'error' : ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}
